// Llegeix el cos d'una Response HTTP de forma robusta i en classifica el
// resultat. Mai llança: si el reverse proxy talla la petició i respon amb
// una pàgina HTML (o un cos buit) en lloc del JSON esperat, `response.json()`
// llançaria un SyntaxError que caldria capturar amb un catch genèric i no
// permetria distingir aquest cas d'un error real de negoci. Amb aquest
// helper, qui crida pot decidir com tractar cada cas (èxit, error conegut,
// resultat indeterminat) sense excepcions inesperades pel mig.
const TRUNCATE_LENGTH = 300;

export type ParsedResponse<T> =
  | { kind: 'json'; ok: boolean; status: number; data: T }
  | { kind: 'non-json'; ok: boolean; status: number; body: string };

// Motiu pel qual una resposta no s'ha pogut interpretar. Es manté com a codi
// (no com a text) perquè qui el mostri el pugui traduir.
export type FailureReason = 'empty' | 'html' | 'malformed' | 'server';

export interface FailureDescription {
  reason: FailureReason;
  status: number;
  code?: string;
  message?: string;
}

/**
 * Extreu d'una resposta fallida la informació útil per explicar a l'usuari
 * QUÈ ha passat, en lloc de deixar-lo amb un error tècnic sense context.
 */
export function describeFailure(parsed: ParsedResponse<unknown>): FailureDescription {
  if (parsed.kind === 'non-json') {
    const body = parsed.body.trim();
    if (body.length === 0) {
      return { reason: 'empty', status: parsed.status };
    }
    // Una pàgina HTML vol dir que qui ha respost és un servidor intermedi
    // (proxy, WAF), no la nostra aplicació.
    return { reason: body.startsWith('<') ? 'html' : 'malformed', status: parsed.status };
  }

  const data = parsed.data as { code?: string; error?: string } | null;
  return {
    reason: 'server',
    status: parsed.status,
    code: data?.code,
    message: data?.error
  };
}

export async function parseJsonResponse<T>(response: Response): Promise<ParsedResponse<T>> {
  const body = await response.text();

  try {
    const data = JSON.parse(body) as T;
    return { kind: 'json', ok: response.ok, status: response.status, data };
  } catch {
    return {
      kind: 'non-json',
      ok: response.ok,
      status: response.status,
      body: body.slice(0, TRUNCATE_LENGTH)
    };
  }
}
